import React, { useContext, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Chip,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import BusinessIcon from '@mui/icons-material/Business';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DataTable from '../../../sharedModules/dataTable';
import PageHeader from '../../../sharedModules/layout/PageHeader';
import { getCollectionsSelectedLocation } from '../../navigation/components/CollectionsLocationToggle';
import { collectionCandidatesQueryKey, useCollectionClients } from '../hooks/useCollectionClients';
import { useUpdateCollectionClient } from '../hooks/useUpdateCollectionClient';
import { CollectionClient, CollectionInvoiceDocument, CollectionInvoicePdf } from '../model';
import InvoicePdfsModal from '../components/InvoicePdfsModal';
import CollectionClientNotesModal from '../components/CollectionClientNotesModal';
import {
  CollectionClientsTableTooltip,
  collectionClientsTooltipProps,
  NOT_EMAILABLE_TOGGLE_TOOLTIP,
  RESIDENTIAL_SUPPRESSED_TOGGLE_TOOLTIP,
  NO_USABLE_EMAIL_TOGGLE_TOOLTIP,
} from '../components/CollectionClientsTableTooltip';
import { SnackBarContext } from '../../../sharedModules/form/contexts/SnackBarContext';
import { formatDollar } from '../utils/format';
import { isResidentialSuppressed } from '../utils/clientEligibility';
import { useCollectionConfig } from '../../config/hooks/useCollectionConfig';
import CollectionClientsAgingSummary, { AgingBucket } from '../components/CollectionClientsAgingSummary';

interface InvoicesModalState {
  open: boolean;
  company: string;
  documents: CollectionInvoiceDocument[];
  acctNo: string;
}

interface NotesModalState {
  open: boolean;
  clientId: number;
  company: string;
}

type TriState = 'all' | 'yes' | 'no';
type ClientTypeFilter = 'all' | 'commercial' | 'residential';

const matchedPdfCount = (documents: CollectionInvoiceDocument[]): number => documents.filter((d) => d.has_pdf).length;

/** Required on cell content for DataTable row clicks (see sharedModules/dataTable/DataTable/TableRow). */
const ROW_CLICK_TAG = '___react-data-table-allow-propagation___';

type DollarColorMode = 'debit' | 'credit' | 'pastDue';

const getDollarColor = (amount: number, mode: DollarColorMode): string => {
  switch (mode) {
    case 'debit':
      return amount > 0 ? 'error.main' : 'text.primary';
    case 'credit':
      return amount < 0 ? 'info.main' : 'text.primary';
    case 'pastDue':
      if (amount > 0) return 'error.main';
      if (amount < 0) return 'info.main';
      return 'text.primary';
    default:
      return 'text.primary';
  }
};

const DollarAmountCell: React.FC<{ amount: number; mode: DollarColorMode }> = ({ amount, mode }) => (
  <Typography variant="body2" sx={{ color: getDollarColor(amount, mode) }} data-tag={ROW_CLICK_TAG}>
    {formatDollar(amount)}
  </Typography>
);

const buildColumns = (
  onInvoicesClick: (company: string, documents: CollectionInvoiceDocument[], acctNo: string) => void,
  onToggleClient: (row: CollectionClient, checked: boolean) => void,
  isToggling: boolean,
  onNotesClick: (row: CollectionClient) => void,
  onViewClick: (row: CollectionClient) => void,
  includeResidential: boolean,
) => [
  {
    name: 'Company',
    selector: (row: CollectionClient) => row.company,
    sortable: true,
    grow: 2,
    cell: (row: CollectionClient) => {
      const noUsableEmail = row.emailable_status === 'no_email';
      const residentialSuppressed = isResidentialSuppressed(row.com_or_res, includeResidential);
      const notEmailable = !row.emailable;
      const toggleDisabled = isToggling || notEmailable || residentialSuppressed;
      const showTooltip = notEmailable || residentialSuppressed;

      let tooltipText = NOT_EMAILABLE_TOGGLE_TOOLTIP;
      if (residentialSuppressed) tooltipText = RESIDENTIAL_SUPPRESSED_TOGGLE_TOOLTIP;
      else if (noUsableEmail) tooltipText = NO_USABLE_EMAIL_TOGGLE_TOOLTIP;

      const isActive = notEmailable || residentialSuppressed ? false : row.active;

      const handleCompanyNameClick = (event: React.MouseEvent) => {
        event.stopPropagation();
        if (toggleDisabled) return;
        onToggleClient(row, !isActive);
      };

      const switchControl = (
        <Switch
          checked={isActive}
          onChange={(event) => onToggleClient(row, event.target.checked)}
          color="primary"
          size="small"
          disabled={toggleDisabled}
          inputProps={{
            'aria-label': `Active for ${row.company}`,
            'aria-disabled': toggleDisabled,
          }}
        />
      );

      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
          {showTooltip ? (
            <span {...collectionClientsTooltipProps(tooltipText)} style={{ display: 'inline-flex' }}>
              {switchControl}
            </span>
          ) : (
            switchControl
          )}
          <Box
            onClick={handleCompanyNameClick}
            sx={{
              flex: 1,
              py: 0.5,
              cursor: toggleDisabled ? 'default' : 'pointer',
            }}
          >
            <Typography variant="body2">{row.company}</Typography>
          </Box>
        </Box>
      );
    },
  },
  { name: 'Bill Code', selector: (row: CollectionClient) => row.bill_code, sortable: true, width: '120px' },
  {
    name: 'Emailable',
    selector: (row: CollectionClient) => row.emailable_status,
    cell: (row: CollectionClient) => {
      if (row.emailable_status === 'no_email') {
        return <Chip label="No Email" color="error" size="small" data-tag={ROW_CLICK_TAG} />;
      }
      if (row.emailable_status === 'no') {
        return <Chip label="No" color="default" size="small" data-tag={ROW_CLICK_TAG} />;
      }
      return <Chip label="Yes" color="success" size="small" data-tag={ROW_CLICK_TAG} />;
    },
    width: '95px',
  },
  {
    name: 'Total Debit',
    selector: (row: CollectionClient) => row.total_debit,
    cell: (row: CollectionClient) => <DollarAmountCell amount={row.total_debit} mode="debit" />,
    sortable: true,
    width: '120px',
  },
  {
    name: 'Total Credit',
    selector: (row: CollectionClient) => row.total_credit,
    cell: (row: CollectionClient) => <DollarAmountCell amount={row.total_credit} mode="credit" />,
    sortable: true,
    width: '120px',
  },
  {
    name: 'Total Past Due',
    selector: 'total_past_due',
    cell: (row: CollectionClient) => <DollarAmountCell amount={row.total_past_due} mode="pastDue" />,
    sortable: true,
    width: '140px',
  },
  {
    name: 'Worst Aging Days',
    selector: (row: CollectionClient) => row.worst_aging_days,
    sortable: true,
    width: '135px',
  },
  {
    name: 'Invoices',
    selector: (row: CollectionClient) => row.invoice_count,
    sortable: true,
    center: true,
    cell: (row: CollectionClient) => {
      if (row.invoice_count === 0) {
        return (
          <Typography variant="body2" data-tag={ROW_CLICK_TAG}>
            0
          </Typography>
        );
      }
      const total = row.invoice_documents.length;
      const matched = matchedPdfCount(row.invoice_documents);
      const allMatched = matched === total;
      return (
        <Chip
          icon={<PictureAsPdfIcon />}
          label={`${matched} / ${total}`}
          size="small"
          color={allMatched ? 'primary' : 'error'}
          variant="outlined"
          onClick={(e) => {
            e.stopPropagation();
            onInvoicesClick(row.company, row.invoice_documents, row.acct_no);
          }}
          sx={{ cursor: 'pointer', px: 1, py: 0.5, height: 'auto' }}
          aria-label={
            allMatched ? `All ${total} invoices have PDFs` : `${matched} of ${total} invoices have PDFs (missing some)`
          }
        />
      );
    },
    width: '120px',
  },
  {
    name: 'Notes',
    selector: (row: CollectionClient) => row.notes_count,
    sortable: true,
    center: true,
    cell: (row: CollectionClient) => (
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          onNotesClick(row);
        }}
        title="View notes"
        aria-label={`${row.notes_count} notes for ${row.company}`}
      >
        <Badge
          badgeContent={row.notes_count}
          color="primary"
          max={99}
          showZero
          sx={{
            '& .MuiBadge-badge': {
              minWidth: 22,
              height: 20,
              padding: '0 6px',
              fontSize: '0.75rem',
              lineHeight: '20px',
              whiteSpace: 'nowrap',
            },
          }}
        >
          <ChatBubbleOutlineIcon fontSize="small" />
        </Badge>
      </IconButton>
    ),
    width: '100px',
  },
  {
    name: '',
    width: '50px',
    center: true,
    cell: (row: CollectionClient) => (
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          onViewClick(row);
        }}
        title="View client details"
        aria-label={`View details for ${row.company}`}
        disabled={!row.collections_client_id}
      >
        <VisibilityIcon fontSize="small" />
      </IconButton>
    ),
  },
];

const filterRows = (
  rows: CollectionClient[],
  customer: string,
  emailable: TriState,
  active: TriState,
  billCode: string,
  clientType: ClientTypeFilter,
): CollectionClient[] => {
  const qCustomer = customer.trim().toLowerCase();
  const qBillCode = billCode.trim().toLowerCase();

  return rows.filter((row) => {
    if (qCustomer && !row.company.toLowerCase().includes(qCustomer)) return false;
    if (emailable !== 'all') {
      const wantYes = emailable === 'yes';
      if (row.emailable !== wantYes) return false;
    }
    if (active !== 'all') {
      const wantYes = active === 'yes';
      if (row.active !== wantYes) return false;
    }
    if (qBillCode && !row.bill_code.toLowerCase().includes(qBillCode)) return false;
    if (clientType !== 'all') {
      const normalized = row.com_or_res?.toUpperCase();
      if (clientType === 'commercial' && normalized !== 'C') return false;
      if (clientType === 'residential' && normalized !== 'R') return false;
    }
    return true;
  });
};

const CollectionClientsListPage: React.FC = () => {
  const history = useHistory();
  const [location] = useState(() => getCollectionsSelectedLocation());
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useCollectionClients(location, true);
  const { updateClient, isLoading: isToggling } = useUpdateCollectionClient();
  const { data: configData, isLoading: isConfigLoading } = useCollectionConfig(location, true);
  const config = configData?.data;
  const tablePending = isLoading || isConfigLoading;
  const { showErrorSnackBar, showSuccessSnackBar } = useContext(SnackBarContext);
  const [clients, setClients] = useState<CollectionClient[]>([]);

  const [customerFilter, setCustomerFilter] = useState('');
  const [emailableFilter, setEmailableFilter] = useState<TriState>('all');
  const [activeFilter, setActiveFilter] = useState<TriState>('all');
  const [billCodeFilter, setBillCodeFilter] = useState('');
  const [clientTypeFilter, setClientTypeFilter] = useState<ClientTypeFilter>('all');
  const [selectedBucket, setSelectedBucket] = useState<AgingBucket | null>(null);

  const [invoicesModal, setInvoicesModal] = useState<InvoicesModalState>({
    open: false,
    company: '',
    documents: [],
    acctNo: '',
  });

  const [notesModal, setNotesModal] = useState<NotesModalState>({
    open: false,
    clientId: 0,
    company: '',
  });

  React.useEffect(() => {
    if (data?.data) {
      setClients(data.data);
    }
  }, [data]);

  const clientsForSummary = useMemo(
    () => filterRows(clients, customerFilter, emailableFilter, activeFilter, billCodeFilter, clientTypeFilter),
    [clients, customerFilter, emailableFilter, activeFilter, billCodeFilter, clientTypeFilter],
  );

  const filteredClients = useMemo(
    () => (selectedBucket ? clientsForSummary.filter((row) => row.tone === selectedBucket) : clientsForSummary),
    [clientsForSummary, selectedBucket],
  );

  const handleClearFilters = () => {
    setCustomerFilter('');
    setEmailableFilter('all');
    setActiveFilter('all');
    setBillCodeFilter('');
    setClientTypeFilter('all');
    setSelectedBucket(null);
  };

  const handleToggleClient = async (client: CollectionClient, active: boolean) => {
    if (!client.collections_client_id) {
      showErrorSnackBar?.('Client not available for update.');
      return;
    }

    const previousActive = client.active;
    setClients((prev) =>
      prev.map((row) => (row.collections_client_id === client.collections_client_id ? { ...row, active } : row)),
    );

    try {
      await updateClient({ id: client.collections_client_id, active });
      showSuccessSnackBar?.(`Client ${active ? 'enabled' : 'disabled'} for scheduled emails.`);
    } catch (error: any) {
      setClients((prev) =>
        prev.map((row) => (row.collections_client_id === client.collections_client_id ? { ...row, active: previousActive } : row)),
      );
      const serverMessage = error?.response?.data?.errors?.[0] || error?.response?.data?.error;
      showErrorSnackBar?.(serverMessage || 'Failed to update client status.');
    }
  };

  const handleInvoicesClick = (company: string, documents: CollectionInvoiceDocument[], acctNo: string) => {
    setInvoicesModal({ open: true, company, documents, acctNo });
  };

  const handleNotesClick = (row: CollectionClient) => {
    if (!row.collections_client_id) return;
    setNotesModal({ open: true, clientId: row.collections_client_id, company: row.company });
  };

  const handleViewClient = (row: CollectionClient) => {
    if (!row.collections_client_id) return;
    history.push(`/collections/clients/${row.collections_client_id}`);
  };

  const handleInvoicePdfUploaded = (applyTo: string, pdf: CollectionInvoicePdf, acctNoForRow: string) => {
    setInvoicesModal((prev) => ({
      ...prev,
      documents: prev.documents.map((d) => (d.apply_to === applyTo ? { ...d, has_pdf: true, pdf } : d)),
    }));
    setClients((prev) =>
      prev.map((row) => {
        if (row.acct_no !== acctNoForRow) return row;
        const nextDocs = row.invoice_documents.map((d) => (d.apply_to === applyTo ? { ...d, has_pdf: true, pdf } : d));
        const matchedPdfs = row.matched_pdfs.some((p) => p.id === pdf.id)
          ? row.matched_pdfs
          : [...row.matched_pdfs, pdf];
        return {
          ...row,
          invoice_documents: nextDocs,
          matched_pdfs: matchedPdfs,
          pdf_count: matchedPdfs.length,
        };
      }),
    );
    void queryClient.invalidateQueries({ queryKey: collectionCandidatesQueryKey(location) });
    showSuccessSnackBar?.('Invoice PDF uploaded.');
  };

  const tableData = isError ? [] : filteredClients;

  return (
    <Grid container>
      <Grid item xs={12}>
        <PageHeader mainTitle="Collections — Clients" titleIcon={<BusinessIcon />} />
      </Grid>
      {isError && (
        <Grid item xs={12} sx={{ mt: 1 }}>
          <Alert severity="error">Failed to load clients.</Alert>
        </Grid>
      )}
      <Grid item xs={12} sx={{ mt: 2 }}>
        <CollectionClientsAgingSummary
          clients={clientsForSummary}
          friendlyThresholdDays={config?.friendly_threshold_days ?? 0}
          secondNoticeThresholdDays={config?.second_notice_threshold_days ?? 0}
          selectedBucket={selectedBucket}
          onSelectBucket={setSelectedBucket}
          loading={isLoading || isConfigLoading || !config}
        />
      </Grid>
      <Grid item xs={12} sx={{ mt: 2 }}>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} md={2}>
            <TextField
              label="Company Name"
              value={customerFilter}
              onChange={(e) => setCustomerFilter(e.target.value)}
              size="small"
              fullWidth
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              label="Bill Code"
              value={billCodeFilter}
              onChange={(e) => setBillCodeFilter(e.target.value)}
              size="small"
              fullWidth
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl size="small" fullWidth>
              <InputLabel id="collections-filter-emailable">Emailable</InputLabel>
              <Select
                labelId="collections-filter-emailable"
                label="Emailable"
                value={emailableFilter}
                onChange={(e) => setEmailableFilter(e.target.value as TriState)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="yes">Yes</MenuItem>
                <MenuItem value="no">No</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl size="small" fullWidth>
              <InputLabel id="collections-filter-active">Active</InputLabel>
              <Select
                labelId="collections-filter-active"
                label="Active"
                value={activeFilter}
                onChange={(e) => setActiveFilter(e.target.value as TriState)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="yes">Yes</MenuItem>
                <MenuItem value="no">No</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl size="small" fullWidth>
              <InputLabel id="collections-filter-client-type">Client Type</InputLabel>
              <Select
                labelId="collections-filter-client-type"
                label="Client Type"
                value={clientTypeFilter}
                onChange={(e) => setClientTypeFilter(e.target.value as ClientTypeFilter)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="commercial">Commercial</MenuItem>
                <MenuItem value="residential">Residential</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md="auto">
            <Button variant="outlined" size="small" onClick={handleClearFilters} sx={{ mb: 0.25 }}>
              Clear filters
            </Button>
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12} sx={{ mt: 2.5 }}>
        <CollectionClientsTableTooltip />
        <Card>
          <DataTable
            columns={buildColumns(
              handleInvoicesClick,
              handleToggleClient,
              isToggling,
              handleNotesClick,
              handleViewClient,
              config?.include_residential ?? false,
            )}
            data={tableData}
            progressPending={tablePending}
            pagination
            paginationPerPage={25}
            paginationRowsPerPageOptions={[25]}
            defaultSortField="total_past_due"
            defaultSortAsc={false}
            highlightOnHover
            pointerOnHover
            onRowClicked={handleViewClient}
            striped
            noHeader
          />
        </Card>
      </Grid>
      <InvoicePdfsModal
        open={invoicesModal.open}
        onClose={() => setInvoicesModal({ open: false, company: '', documents: [], acctNo: '' })}
        company={invoicesModal.company}
        documents={invoicesModal.documents}
        location={location}
        acctNo={invoicesModal.acctNo}
        onInvoicePdfUploaded={handleInvoicePdfUploaded}
      />
      <CollectionClientNotesModal
        open={notesModal.open}
        onClose={() => setNotesModal({ open: false, clientId: 0, company: '' })}
        clientId={notesModal.clientId}
        company={notesModal.company}
        onNotesChanged={() => {
          void queryClient.invalidateQueries({ queryKey: collectionCandidatesQueryKey(location) });
        }}
      />
    </Grid>
  );
};

export default CollectionClientsListPage;
